/**
 * "Shares & overall rows": the primary-breakdown tools, folded away under the breakdowns because most queries never
 * need them. Picking "Group by" breakdowns (the who: a team, a player) marks them primary; the rest then describe it,
 * and three things open up: a share column (each row's % of its group's total), one Overall row per group, and
 * hiding a breakdown row that only repeats its Overall row (an on/off split's other teams).
 *
 * Opens on its own when a loaded query already uses any of it.
 */
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { MetricGroup } from "../../../models/Explorer";

interface Props {
  metricGroup: MetricGroup;
  metrics: string[];
  onChange: (next: MetricGroup) => void;
}

const LABEL_SX = {
  fontSize: "0.75rem",
  textTransform: "uppercase",
  fontWeight: 600,
  color: "rgba(0,0,0,0.6)",
  mb: 0.5,
} as const;

const CHECK_LABEL_SX = {
  ml: 0,
  "& .MuiTypography-root": { fontSize: "0.8rem", color: "rgba(0,0,0,0.6)" },
} as const;

export function groupingSummary(mg: MetricGroup): string | null {
  const groupBy = mg.dimensions.filter((d) => d.primary && d.name).map((d) => d.name!.value);
  if (groupBy.length === 0) return null;
  const parts = [`Grouped by ${groupBy.join(", ")}`];
  if (mg.dimensionRatio?.value) parts.push(`% of ${mg.dimensionRatio.value}`);
  if (mg.includeOverall) parts.push("Overall rows");
  if (mg.includeOverall && mg.hideIdenticalOverall) parts.push("repeats hidden");
  return parts.join(" · ");
}

export default function GroupingSection({ metricGroup, metrics, onChange }: Props) {
  const named = metricGroup.dimensions.filter((d) => d.name?.value);
  const groupBy = named.filter((d) => d.primary).map((d) => d.name!.value);
  const inUse =
    groupBy.length > 0 || !!metricGroup.dimensionRatio || !!metricGroup.includeOverall;
  const [open, setOpen] = useState(inUse);
  // A loaded query that uses it opens the section; closing it again is the reader's call
  useEffect(() => {
    if (inUse) setOpen(true);
  }, [inUse]);

  const setGroupBy = (names: string[]) => {
    const picked = new Set(names);
    onChange({
      ...metricGroup,
      dimensions: metricGroup.dimensions.map((d) => ({
        ...d,
        primary: (d.name?.value && picked.has(d.name.value)) || undefined,
      })),
    });
  };
  const active = groupBy.length > 0;
  // Every breakdown primary leaves nothing to group within
  const allPicked = groupBy.length >= named.length;
  const summary = groupingSummary(metricGroup);

  return (
    <Box mt={1}>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        sx={{ textTransform: "none", fontSize: "0.8rem", color: "rgba(0,0,0,0.6)", px: 0.5 }}
      >
        Shares & overall rows
      </Button>
      {!open && summary && (
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", ml: 0.5 }}>
          {summary}
        </Typography>
      )}
      <Collapse in={open}>
        <Box
          mt={0.5}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
            backgroundColor: "grey.50",
          }}
        >
          <Typography sx={LABEL_SX}>Group by</Typography>
          <Autocomplete
            multiple
            size="small"
            options={named.map((d) => d.name!.value)}
            value={groupBy}
            onChange={(_e, value) => setGroupBy(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={groupBy.length ? undefined : "Who the rows are about (e.g. TeamOffense)"}
              />
            )}
          />
          {allPicked && active && (
            <Typography sx={{ fontSize: "0.75rem", color: "warning.main", mt: 0.5 }}>
              Leave at least one breakdown out of the group, or there is nothing to group within.
            </Typography>
          )}

          <Typography sx={{ ...LABEL_SX, mt: 1.25 }}>Share of</Typography>
          <Autocomplete
            size="small"
            disabled={!active}
            options={metrics}
            value={metricGroup.dimensionRatio?.value ?? null}
            onChange={(_e, value) =>
              onChange({
                ...metricGroup,
                dimensionRatio: value ? { value, label: value } : null,
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="% of metric (e.g. Poss)"
              />
            )}
          />

          <FormControlLabel
            disabled={!active}
            control={
              <Checkbox
                size="small"
                checked={!!metricGroup.includeOverall}
                onChange={(e) =>
                  onChange({ ...metricGroup, includeOverall: e.target.checked })
                }
                sx={{ py: 0, "& .MuiSvgIcon-root": { fontSize: "1rem" } }}
              />
            }
            label="Overall row for each group"
            sx={{ ...CHECK_LABEL_SX, mt: 0.75 }}
          />
          <FormControlLabel
            disabled={!active || !metricGroup.includeOverall}
            control={
              <Checkbox
                size="small"
                checked={!!metricGroup.hideIdenticalOverall}
                onChange={(e) =>
                  onChange({ ...metricGroup, hideIdenticalOverall: e.target.checked })
                }
                sx={{ py: 0, "& .MuiSvgIcon-root": { fontSize: "1rem" } }}
              />
            }
            label="Hide rows identical to their Overall"
            sx={CHECK_LABEL_SX}
          />
          {!active && (
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>
              Pick who the rows are about to turn these on.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
